import React, { useState, useMemo, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Phone, Mail, User, CheckCircle2, Calendar as CalendarIcon, Clock } from 'lucide-react';
import './BookingModal.css';

const NairobiOffset = 3; // UTC+3

const BookingModal = ({ isOpen, onClose, sessionId, likedVisuals = [], selectedSamples = [], onBookingSuccess, showroom = "nairobi" }) => {
    const [step, setStep] = useState(1);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [contactInfo, setContactInfo] = useState({ name: '', phone: '', email: '' });
    const [wantsCall, setWantsCall] = useState(null);
    const [prepareSamples, setPrepareSamples] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Get current date in Nairobi time for comparison
    const getNairobiNow = () => {
        const now = new Date();
        const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
        return new Date(utc + (3600000 * NairobiOffset));
    };

    const nairobiNow = getNairobiNow();

    const businessHoursNairobi = {
        0: null,
        1: { start: 7.5,  end: 16.5, label: "7:30 AM - 4:30 PM" },
        2: { start: 7.5,  end: 18.0, label: "7:30 AM - 6:00 PM" },
        3: { start: 7.5,  end: 18.0, label: "7:30 AM - 6:00 PM" },
        4: { start: 7.5,  end: 18.0, label: "7:30 AM - 6:00 PM" },
        5: { start: 7.5,  end: 18.0, label: "7:30 AM - 6:00 PM" },
        6: { start: 8.0,  end: 16.0, label: "8:00 AM - 4:00 PM" },
    };

    const businessHoursDiani = {
        0: null,
        1: { start: 10.0, end: 19.0, label: "10:00 AM - 7:00 PM" },
        2: { start: 10.0, end: 19.0, label: "10:00 AM - 7:00 PM" },
        3: { start: 10.0, end: 19.0, label: "10:00 AM - 7:00 PM" },
        4: { start: 10.0, end: 19.0, label: "10:00 AM - 7:00 PM" },
        5: { start: 10.0, end: 19.0, label: "10:00 AM - 7:00 PM" },
        6: { start: 9.0,  end: 17.0, label: "9:00 AM - 5:00 PM" },
    };

    const businessHours = showroom === "diani" ? businessHoursDiani : businessHoursNairobi;

    const showroomLabel = showroom === "diani" ? "Diani · Pioneer Mall" : "Nairobi · Ridgeways";

    const openingHoursBanner = showroom === "diani"
        ? "Mon - Fri: 10:00 AM - 7:00 PM | Sat: 9:00 AM - 5:00 PM | Sun: Closed"
        : "Mon: 7:30 AM - 4:30 PM | Tue - Fri: 7:30 AM - 6:00 PM | Sat: 8:00 AM - 4:00 PM | Sun: Closed";

    const calendarDays = useMemo(() => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const days = [];
        // Pad with empty days for previous month
        for (let i = 0; i < firstDay; i++) {
            days.push(null);
        }
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(new Date(year, month, i));
        }
        return days;
    }, [currentMonth]);

    const getTimeSlots = useCallback((date) => {
        if (!date) return [];
        const day = date.getDay();
        const hours = businessHours[day];
        if (!hours) return [];

        const slots = [];
        for (let h = hours.start; h < hours.end; h += 1) {
            const isToday = date.toDateString() === nairobiNow.toDateString();
            // If today, only show slots in the future
            if (isToday && h <= nairobiNow.getHours() + (nairobiNow.getMinutes() / 60)) {
                continue;
            }

            const period = h < 12 ? 'AM' : 'PM';
            const displayHour = h % 12 === 0 ? 12 : Math.floor(h % 12);
            const minutes = (h % 1) * 60 === 0 ? '00' : '30'; // Simple hour slots for now
            slots.push({
                time: h,
                label: `${displayHour}:${minutes} ${period}`
            });
        }
        return slots;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nairobiNow]);

    const handleConfirmBooking = async () => {
        setIsSubmitting(true);
        const dateStr = selectedDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
        const timeStr = selectedTime.label;

        const favoriteList = likedVisuals.map(v => `- ${v.label} (${v.brandName || 'DEKTON'})`).join('\n');
        const cartList = selectedSamples.map(s => `- ${s.name || s.productName} (${s.brandName || 'DEKTON'})`).join('\n');

        const showroomStr = showroom === "diani" ? "Diani (Pioneer Mall, 1st Floor)" : "Nairobi (Woodvale Lane, Ridgeways)";
        const message = `BOOKING REQUEST\n\nShowroom: ${showroomStr}\nDate: ${dateStr}\nTime: ${timeStr}\nName: ${contactInfo.name}\nFollow-up call: ${wantsCall === 'yes' ? 'Yes' : 'No'}\nPhone: ${contactInfo.phone}${contactInfo.email ? `\nEmail: ${contactInfo.email}` : ''}\nSamples prepared: ${prepareSamples === 'yes' ? 'Yes' : 'No'}${favoriteList ? `\n\nFavorite Colors:\n${favoriteList}` : ''}${cartList ? `\n\nCart Colors:\n${cartList}` : ''}`;

        const payload = {
            intent: "booking",
            sessionId: sessionId,
            message: message,
            timestamp: new Date().toISOString(),
            booking: {
                date: dateStr,
                time: timeStr,
                name: contactInfo.name,
                phone: contactInfo.phone,
                email: contactInfo.email || "",
                followUpCall: wantsCall === 'yes',
                prepareSamples: prepareSamples === 'yes',
                showroom: showroom === "diani" ? "Diani" : "Nairobi",
                favoriteColors: likedVisuals.map(v => v.label),
                cartColors: selectedSamples.map(s => s.name || s.productName)
            }
        };

        try {
            await fetch("https://elegantfittings.app.n8n.cloud/webhook/ef-chatbot", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });
            setStep(6); // Success
            if (onBookingSuccess) {
                onBookingSuccess(message);
            }
        } catch (error) {
            console.error("Booking error:", error);
            alert("Sorry, there was an error submitting your booking. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    const renderStep = () => {
        switch (step) {
            case 1: // Date Selection
                return (
                    <div className="booking-step">
                        <h3>Select a date</h3>
                        <div className="calendar-header">
                            <button onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)))}>
                                <ChevronLeft size={20} />
                            </button>
                            <span className="current-month">
                                {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                            </span>
                            <button onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)))}>
                                <ChevronRight size={20} />
                            </button>
                        </div>
                        <div className="calendar-grid">
                            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => <div key={d} className="calendar-day-label">{d}</div>)}
                            {calendarDays.map((date, idx) => {
                                if (!date) return <div key={`empty-${idx}`} className="calendar-day-empty" />;
                                const isPast = date < new Date(nairobiNow.toDateString());
                                const isSunday = date.getDay() === 0;
                                const isSelected = selectedDate?.toDateString() === date.toDateString();
                                const isDisabled = isPast || isSunday;
                                return (
                                    <button
                                        key={idx}
                                        className={`calendar-day-btn ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                                        disabled={isDisabled}
                                        onClick={() => { setSelectedDate(date); setStep(2); }}
                                    >
                                        {date.getDate()}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                );
            case 2: // Time Selection
                const slots = getTimeSlots(selectedDate);
                return (
                    <div className="booking-step">
                        <h3>Select a time</h3>
                        <p className="selected-date-preview">{selectedDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                        <div className="time-slots-grid">
                            {slots.length > 0 ? slots.map((slot, idx) => (
                                <button
                                    key={idx}
                                    className={`time-slot-btn ${selectedTime?.label === slot.label ? 'selected' : ''}`}
                                    onClick={() => setSelectedTime(slot)}
                                >
                                    {slot.label}
                                </button>
                            )) : <p className="no-slots">No available slots for this day.</p>}
                        </div>
                        <div className="step-actions">
                            <button className="back-btn" onClick={() => setStep(1)}>Back</button>
                            <button className="next-btn" disabled={!selectedTime} onClick={() => setStep(3)}>Next</button>
                        </div>
                    </div>
                );
            case 3: // Name Entry
                return (
                    <div className="booking-step">
                        <h3>Enter your name</h3>
                        <p className="selected-date-preview">{selectedDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} at {selectedTime?.label}</p>
                        <div className="booking-input-wrap">
                            <label>Your Name</label>
                            <div className="input-with-icon">
                                <User size={18} />
                                <input
                                    type="text"
                                    placeholder="Enter your full name"
                                    value={contactInfo.name}
                                    onChange={(e) => setContactInfo({ ...contactInfo, name: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="step-actions">
                            <button className="back-btn" onClick={() => setStep(2)}>Back</button>
                            <button className="next-btn" disabled={!contactInfo.name} onClick={() => setStep(4)}>Next</button>
                        </div>
                    </div>
                );
            case 4: // Confirmation Call
                return (
                    <div className="booking-step">
                        <h3>Confirm details</h3>
                        <p className="booking-prompt">Would you like us to call you before your visit for confirmation?</p>
                        <div className="booking-options">
                            <button className={`option-btn ${wantsCall === 'yes' ? 'selected' : ''}`} onClick={() => setWantsCall('yes')}>
                                <div className="radio-circle"></div>
                                <span>Yes, please call me</span>
                            </button>
                            <button className={`option-btn ${wantsCall === 'no' ? 'selected' : ''}`} onClick={() => setWantsCall('no')}>
                                <div className="radio-circle"></div>
                                <span>No, I'll just show up</span>
                            </button>
                        </div>
                        {wantsCall === 'yes' && (
                            <div className="booking-sub-inputs">
                                <p className="sub-prompt">Please provide your contact details for the follow-up call.</p>
                                <div className="booking-input-wrap small">
                                    <label>Phone Number *</label>
                                    <div className="input-with-icon">
                                        <Phone size={18} />
                                        <input
                                            type="tel"
                                            placeholder="+254 7XX XXX XXX"
                                            value={contactInfo.phone}
                                            onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="booking-input-wrap small">
                                    <label>Email (optional)</label>
                                    <div className="input-with-icon">
                                        <Mail size={18} />
                                        <input
                                            type="email"
                                            placeholder="you@example.com"
                                            value={contactInfo.email}
                                            onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="step-actions">
                            <button className="back-btn" onClick={() => setStep(3)}>Back</button>
                            <button className="next-btn" disabled={!wantsCall || (wantsCall === 'yes' && !contactInfo.phone)} onClick={() => setStep(5)}>Next</button>
                        </div>
                    </div>
                );
            case 5: // Sample Preparation
                const hasSelections = likedVisuals.length > 0 || selectedSamples.length > 0;
                return (
                    <div className="booking-step">
                        <h3>Sample preparation</h3>
                        <div className="sample-prep-info">
                            <div className="info-icon"><CalendarIcon size={20} /></div>
                            <div className="info-text">
                                <p className="main-info">Would you like us to prepare samples for your visit?</p>
                                <p className="sub-info">Based on your selections, we can have these ready for you.</p>
                            </div>
                        </div>
                        {hasSelections && (
                            <div className="current-selections-card">
                                {likedVisuals.length > 0 && (
                                    <>
                                        <h4>Favorite Colors:</h4>
                                        <ul>
                                            {likedVisuals.map((v, i) => <li key={`fav-${i}`}>{v.label} ({v.brandName || 'DEKTON'})</li>)}
                                        </ul>
                                    </>
                                )}
                                {selectedSamples.length > 0 && (
                                    <>
                                        <h4>Cart Colors:</h4>
                                        <ul>
                                            {selectedSamples.map((s, i) => <li key={`cart-${i}`}>{s.name || s.productName} ({s.brandName || 'DEKTON'})</li>)}
                                        </ul>
                                    </>
                                )}
                            </div>
                        )}
                        <div className="booking-options">
                            <button className={`option-btn ${prepareSamples === 'yes' ? 'selected' : ''}`} onClick={() => setPrepareSamples('yes')}>
                                <div className="radio-circle"></div>
                                <span>Yes, prepare these samples</span>
                            </button>
                            <button className={`option-btn ${prepareSamples === 'no' ? 'selected' : ''}`} onClick={() => setPrepareSamples('no')}>
                                <div className="radio-circle"></div>
                                <span>No, I'll browse on arrival</span>
                            </button>
                        </div>
                        <div className="step-actions">
                            <button className="back-btn" onClick={() => setStep(4)}>Back</button>
                            <button className="confirm-btn" disabled={!prepareSamples || isSubmitting} onClick={handleConfirmBooking}>
                                {isSubmitting ? 'Confirming...' : 'Confirm Booking'}
                            </button>
                        </div>
                    </div>
                );
            case 6: // Success
                return (
                    <div className="booking-step success">
                        <CheckCircle2 size={64} color="#10b981" />
                        <h3>Booking Request Sent!</h3>
                        <p>Thank you, {contactInfo.name}. We've received your request for {selectedDate?.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} at {selectedTime?.label}.</p>
                        <p className="success-sub">We will contact you shortly to confirm the details.</p>
                        <button className="finish-btn" onClick={onClose}>Done</button>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="booking-modal-overlay">
            <div className="booking-modal-content">
                <div className="booking-modal-header">
                    <div className="header-top">
                        <div>
                            <h2>Book a Showroom Visit</h2>
                            <p className="showroom-label">{showroomLabel}</p>
                        </div>
                        <button className="close-booking-btn" onClick={onClose}><X size={24} /></button>
                    </div>
                    <p className="step-indicator">
                        {step === 1 ? 'Select a date' : step === 2 ? 'Select a time' : step === 3 ? 'Enter your name' : step === 4 ? 'Confirm details' : step === 5 ? 'Sample preparation' : 'Complete'}
                    </p>
                </div>

                <div className="hours-banner">
                    <Clock size={18} />
                    <div className="hours-text">
                        <strong>Opening Hours</strong>
                        <p>{openingHoursBanner}</p>
                    </div>
                </div>

                <div className="booking-modal-body">
                    {renderStep()}
                </div>
            </div>
        </div>
    );
};

export default BookingModal;
